import { Col, Image, Modal, Row } from "antd";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router";
import {
  getNoticeList,
  getSpecialNoticeList,
  postNoticeSurveyReply,
} from "../../../apis/Notice/apis";
import { ReactComponent as ModalClose } from "../../../assets/icons/ModalCloseBtn.svg";
import useSpecial from "../../../hooks/useSpecial";
import useUser from "../../../hooks/useUsers";
import { alertError } from "../../../shared/utils";
import { UtilNotice } from "../../../shared/utils/Notice";
import Button from "../../atoms/Button";
import ModalTitle from "../../molecures/Modals/styles/ModalTitle";
import { ModalBody, ModalConfirmTitle, ModalContainer } from "./Modal.styled";
import { ResultModal } from "./ResultModal";

interface ModalNoticeProps {}

/**
 *  팝업 공지사항 컴포넌트
 * @returns
 */
export const ModalNotice: React.FC<ModalNoticeProps> = () => {
  const [modal, contextHolder] = Modal.useModal();
  const { getUserID } = useUser();
  const { special } = useSpecial();

  const [show, setShow] = useState<{ [key: number]: boolean }>({});
  const nav = useNavigate();

  const { data } = useQuery(
    ["ShowModalNotice", getUserID()],
    () =>
      special
        ? getSpecialNoticeList({ isPopupExposure: true, page: 1, pageSize: 10 })
        : getNoticeList({ isPopupExposure: true, page: 1, pageSize: 10 }),
    { enabled: !!getUserID(), select: (data) => data.rows.reverse() || [] }
  );

  const { mutateAsync, isLoading } = useMutation(postNoticeSurveyReply);

  /**
   * 공지사항 보러가기 핸들러
   * @param id
   */
  const handleOk = (id: number) => {
    const prefix = special.includes("speedMateCode")
      ? "/speed-mate"
      : special.includes("autooasisCode")
      ? "/autooasis"
      : "";
    const path = `${prefix}/notices/${id as number}`;
    nav(path);

    // 공지사항 보러가기 시 모든 모달 false
    const visibles = { ...show };

    const initValue: { [key: number]: boolean } = {};
    const allHide = Object.keys(visibles).reduce((a, b: string) => {
      a[+b] = false;
      return a;
    }, initValue);

    setShow(allHide);
  };

  /**
   * 닫기 이벤트 핸들러 ( X 버튼)
   * @param id
   */
  const handleCancel = (id: number) => {
    setShow((prev) => ({ ...prev, [id]: false }));
  };

  /**
   * 오늘하루 그만보기 이벤트 핸들러
   * @param id
   */
  const handleNotShowToday = (id: number) => {
    UtilNotice.setShowInfo(getUserID(), id);
    setShow((prev) => ({ ...prev, [id]: false }));
  };

  const handleServey = (id: number, acceptYn: boolean) => {
    mutateAsync({ id, acceptYn })
      .then(() => {
        modal.success({
          title: <ModalConfirmTitle>정비가능항목 변경 완료</ModalConfirmTitle>,
          content: <ResultModal acceptYn={acceptYn} />,
          icon: false,
          centered: true,
          okButtonProps: { size: "large" },
          open: true,
          onOk: () => {
            handleCancel(id);
          },
        });
      })
      .catch((e: any) => {
        alertError(e.message);
      });
  };

  useEffect(() => {
    const userId = getUserID();
    const initValue: { [key: number]: boolean } = {};
    const visibles =
      data?.reduce((a, b) => {
        let status = true;
        const id = b.id as number;

        /**
         * 팝업 세팅 정보를가져오거나, 만료정보 가져오거나
         */
        const info = UtilNotice.getShowInfo(userId, b.id as number);

        if (info) {
          const checked = UtilNotice.checkExpiredInfo(userId, id);
          if (!checked) {
            status = false;
          }
        }

        a[id] = status;

        return a;
      }, initValue) || {};

    setShow(visibles);
  }, [data]);

  return (
    <>
      {data?.map((item, index) => (
        <Modal
          key={`notice:${index}:${item.id}`}
          centered
          visible={show[item.id as number]}
          title={<ModalTitle>{item.title || "공지사항"}</ModalTitle>}
          okText="공지사항 확인하기"
          cancelText="오늘하루 그만보기"
          width={940}
          onOk={() => handleOk(item.id as number)}
          onCancel={() => handleCancel(item.id as number)}
          destroyOnClose
          closeIcon={
            <ModalClose
              style={{ verticalAlign: "-1.05em", marginRight: "1.05em" }}
            />
          }
          bodyStyle={{ padding: 0 }}
          footer={
            <Row
              justify="center"
              align="middle"
              style={{ padding: "8px 8px" }}
              gutter={12}
            >
              {item.isSurvey === true && item.noticeHistories && (
                <>
                  <Col>
                    <Button
                      onClick={() => handleServey(item.id as number, false)}
                      type="default"
                      size="large"
                      loading={isLoading}
                    >
                      동의안함
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      onClick={() => handleServey(item.id as number, true)}
                      type="primary"
                      size="large"
                      loading={isLoading}
                    >
                      동의하기
                    </Button>
                  </Col>
                </>
              )}
              {!item.isSurvey && (
                <>
                  <Col>
                    <Button
                      onClick={() => handleNotShowToday(item.id as number)}
                      type="default"
                      size="large"
                    >
                      오늘하루 그만보기
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      onClick={() => handleOk(item.id as number)}
                      type="primary"
                      size="large"
                    >
                      자세히보기
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          }
        >
          <ModalContainer
            style={{
              maxHeight: 760,
              overflowY: "scroll",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {item.title !== "매장 친절 리뷰 이벤트" && (
              <ModalBody dangerouslySetInnerHTML={{ __html: item.body }} />
            )}
            {item.title === "매장 친절 리뷰 이벤트" && (
              <Image
                src="/images/events/review.png"
                preview={false}
                width={636}
                height={636}
              />
            )}
          </ModalContainer>
        </Modal>
      ))}
      {contextHolder}
    </>
  );
};
